import React from "react";
import { motion } from "framer-motion";
import { Zap, Activity, HeartPulse, Target } from "lucide-react";

const SPEED_IMG =
  "https://images.unsplash.com/photo-1630476438396-cc360a4b52ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxhdGhsZXRlJTIwdHJhaW5pbmclMjBhZ2lsaXR5JTIwc3BlZWQlMjB0cmFja3xlbnwwfHx8fDE3Nzc2Njc1NTN8MA&ixlib=rb-4.1.0&q=85";
const MECH_IMG =
  "https://images.unsplash.com/photo-1709315957145-a4bad1feef28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxneW0lMjBiYXJiZWxsJTIwaGVhdnklMjBsaWZ0aW5nJTIwZGFya3xlbnwwfHx8fDE3Nzc2Njc1NjV8MA&ixlib=rb-4.1.0&q=85";
const RECOVERY_IMG =
  "https://images.unsplash.com/photo-1749131871347-6d211039ac3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxhdGhsZXRlJTIwcmVjb3ZlcnklMjBwaHlzaWNhbCUyMHRoZXJhcHklMjBtYXNzYWdlfGVufDB8fHx8MTc3NzY2NzU1M3ww&ixlib=rb-4.1.0&q=85";

const MotionDiv = motion.div;

function Card({ children, className = "", testId }) {
  return (
    <MotionDiv
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      whileHover={{ y: -4 }}
      className={`relative border border-zinc-800 bg-[#141414] overflow-hidden group transition-colors hover:border-[#E5B43A] ${className}`}
      data-testid={testId}
    >
      {children}
    </MotionDiv>
  );
}

export default function Services() {
  return (
    <section id="services" className="relative py-24 md:py-32 px-6 md:px-12" data-testid="services-section">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div>
            <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#E5B43A] mb-4">
              // Services
            </div>
            <h2 className="font-display text-4xl md:text-6xl tracking-tight">
              Train with <span className="text-[#E5B43A]">intent.</span>
            </h2>
          </div>
          <p className="max-w-md text-zinc-400 text-base leading-relaxed">
            Four pillars. One performance system. Every session designed to
            translate directly to the field, the track, or real life.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {/* Speed & Agility - large image card */}
          <Card className="md:col-span-8 md:row-span-2 min-h-[420px] md:min-h-[560px]" testId="service-speed">
            <div
              className="absolute inset-0 bg-cover bg-center img-grayscale"
              style={{ backgroundImage: `url(${SPEED_IMG})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-transparent" />
            <div className="relative z-10 h-full p-8 md:p-10 flex flex-col justify-end">
              <Zap className="text-[#E5B43A] mb-6" size={32} strokeWidth={2} />
              <div className="text-xs font-bold tracking-[0.3em] uppercase text-zinc-400 mb-3">01 / Pillar</div>
              <h3 className="font-display text-4xl md:text-5xl mb-4">Speed & Agility</h3>
              <p className="text-zinc-300 max-w-lg leading-relaxed">
                Linear acceleration. Lateral quickness. Reactive footwork.
                Built through measured sprint work, cone drills, and plyometric
                progressions designed for measurable output.
              </p>
            </div>
          </Card>

          {/* Body Mechanics - text+image card */}
          <Card className="md:col-span-4 min-h-[260px]" testId="service-mechanics">
            <div
              className="absolute inset-0 bg-cover bg-center img-grayscale opacity-40"
              style={{ backgroundImage: `url(${MECH_IMG})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 to-[#0A0A0A]" />
            <div className="relative z-10 h-full p-8 flex flex-col justify-between">
              <Activity className="text-[#E5B43A]" size={28} strokeWidth={2} />
              <div>
                <div className="text-xs font-bold tracking-[0.3em] uppercase text-zinc-400 mb-2">02 / Pillar</div>
                <h3 className="font-display text-3xl mb-3">Body Mechanics</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Re-engineer your movement patterns. Build strength from
                  clean positions.
                </p>
              </div>
            </div>
          </Card>

          {/* Recovery */}
          <Card className="md:col-span-4 min-h-[260px]" testId="service-recovery">
            <div
              className="absolute inset-0 bg-cover bg-center img-grayscale opacity-35"
              style={{ backgroundImage: `url(${RECOVERY_IMG})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 to-[#0A0A0A]" />
            <div className="relative z-10 h-full p-8 flex flex-col justify-between">
              <HeartPulse className="text-[#E5B43A]" size={28} strokeWidth={2} />
              <div>
                <div className="text-xs font-bold tracking-[0.3em] uppercase text-zinc-400 mb-2">03 / Pillar</div>
                <h3 className="font-display text-3xl mb-3">Recovery</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Mobility, soft tissue work, and sleep protocols. Recover
                  faster, train harder, last longer.
                </p>
              </div>
            </div>
          </Card>

          {/* Online Training CTA card */}
          <Card className="md:col-span-12 min-h-[220px]" testId="service-online">
            <div className="relative z-10 h-full p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-start gap-6">
                <Target className="text-[#E5B43A] mt-1 shrink-0" size={32} strokeWidth={2} />
                <div>
                  <div className="text-xs font-bold tracking-[0.3em] uppercase text-zinc-400 mb-2">04 / Platform</div>
                  <h3 className="font-display text-3xl md:text-4xl mb-2">Online Training — Anywhere</h3>
                  <p className="text-zinc-400 max-w-2xl leading-relaxed">
                    Custom programs delivered to your phone. Video feedback,
                    macro check-ins, and a plan built around your schedule and
                    equipment.
                  </p>
                </div>
              </div>
              <a
                href="#contact"
                className="shrink-0 inline-flex items-center justify-center bg-[#E5B43A] text-black font-bold uppercase tracking-widest px-6 py-4 text-xs hover:bg-white hover:text-[#0A0A0A] transition-colors duration-300"
                data-testid="service-online-cta"
              >
                Apply Now
              </a>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}
